/**
 * Client side of cross-browser Try draft recovery (Vidi-atl.22.2).
 *
 * The signup edge function parks the selected cards server-side. After the learner
 * confirms their email in ANOTHER browser, DataLoader claims them here for the signed-in
 * account only. The draft is handed over in memory (never localStorage, so another account
 * on this browser can't pick it up) and the server copy is deleted only after every card is
 * durable. Every call is best effort: recovery must never break loading the app, and no call
 * claims a server copy is gone unless the database said so.
 */
import { supabase } from '@/lib/supabase';
import { getLanguagePair } from '@/config/languages';
import { reportError } from '@/services/errorTracking';
import type { AnonymousCard, RecoveredDraft } from '@/utils/anonymousSession';

export interface ClaimedDraft {
  draft: RecoveredDraft;
  lease: string;
}

/**
 * none: the database says no usable draft waits for this account right now.
 * busy: a draft waits but another tab or an earlier unfinished run holds the lease.
 * error: the answer is unknown (network, RPC or an unreadable reply). Never the same as none.
 */
export type ClaimResult =
  | ({ status: 'claimed' } & ClaimedDraft)
  | { status: 'none' }
  | { status: 'busy' }
  | { status: 'error' };

/** The lease is 2 minutes; renewing at 45s keeps a slow import alive without holding it forever. */
const RENEW_EVERY_MS = 45_000;

/**
 * A draft is stored at signup and served for 7 days. An account whose Auth creation date is
 * clearly older than that (plus an hour for request and clock delay) cannot have a servable
 * draft, so DataLoader skips the claim round trip. This is a performance hint only: a missing,
 * invalid, future or non-string date answers false so recovery is still attempted, and no
 * mutable user metadata is ever read.
 */
const RECOVERY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000;
export function isPastRecoveryWindow(authCreatedAt: unknown, now: number = Date.now()): boolean {
  if (typeof authCreatedAt !== 'string' || !authCreatedAt) return false;
  const created = Date.parse(authCreatedAt);
  return Number.isFinite(created) && now - created > RECOVERY_WINDOW_MS;
}

/**
 * Every call names the account the browser believes is signed in (p_expected_user_id). The
 * database acts only when its own JWT is exactly that account, so a browser that switches
 * accounts between a cache check and the request gets nothing instead of another learner's draft.
 */
export async function claimPendingTryDraft(expectedUserId: string): Promise<ClaimResult> {
  try {
    const { data, error } = await supabase.rpc('claim_pending_try_draft', {
      p_expected_user_id: expectedUserId,
    });
    if (error || !data || typeof data !== 'object') return { status: 'error' };
    const reply = data as {
      status?: unknown;
      payload?: Record<string, unknown>;
      lease?: unknown;
    };
    if (reply.status === 'none') return { status: 'none' };
    if (reply.status === 'busy') return { status: 'busy' };
    const { payload, lease } = reply;
    if (
      reply.status !== 'claimed' ||
      !payload ||
      typeof lease !== 'string' ||
      !Array.isArray(payload.cards)
    ) {
      return { status: 'error' };
    }
    const languagePair = String(payload.languagePair ?? '');
    const pair = getLanguagePair(languagePair);
    return {
      status: 'claimed',
      lease,
      draft: {
        id: String(payload.sessionId ?? ''),
        languagePair,
        cefrLevel: typeof payload.cefrLevel === 'string' ? payload.cefrLevel : null,
        targetLanguage: pair?.target ?? '',
        nativeLanguage: pair?.native ?? '',
        cards: (payload.cards as Record<string, unknown>[]).map((c) => ({
          ...(c as unknown as AnonymousCard),
          createdAt: '',
        })),
      },
    };
  } catch (err) {
    reportError(err, { operation: 'pendingTryDraft:claim' });
    return { status: 'error' };
  }
}

/** Keeps the lease alive while `work` runs. Returns work's result. */
export async function withLeaseRenewal<T>(
  expectedUserId: string,
  lease: string,
  work: () => Promise<T>,
): Promise<T> {
  const timer = setInterval(() => {
    void Promise.resolve(
      supabase.rpc('renew_pending_try_draft', {
        p_expected_user_id: expectedUserId,
        p_lease: lease,
      }),
    ).catch(() => undefined);
  }, RENEW_EVERY_MS);
  try {
    return await work();
  } finally {
    clearInterval(timer);
  }
}

/** True only when the database answered true; a false reply, an error or a throw is false. */
async function call(
  name: 'finish_pending_try_draft' | 'release_pending_try_draft' | 'acknowledge_local_try_draft',
  args: Record<string, string>,
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc(name, args);
    if (error) {
      reportError(new Error(error.message), { operation: `pendingTryDraft:${name}` });
      return false;
    }
    return data === true;
  } catch (err) {
    reportError(err, { operation: `pendingTryDraft:${name}` });
    return false;
  }
}
/**
 * After saved/already_saved of a RECOVERED run: deletes the server copy. The database refuses
 * unless every card of the draft has its own durable import-key row, so false can also mean
 * "not all cards are proven durable".
 */
export const finishPendingTryDraft = (expectedUserId: string, lease: string) =>
  call('finish_pending_try_draft', { p_expected_user_id: expectedUserId, p_lease: lease });
/** Any other outcome: keeps the copy and lets the next load retry immediately. */
export const releasePendingTryDraft = (expectedUserId: string, lease: string) =>
  call('release_pending_try_draft', { p_expected_user_id: expectedUserId, p_lease: lease });
/**
 * After this browser's OWN draft imported completely: deletes only the waiting copy of that
 * same session (if every card is durable by import key or legacy word pair), so it cannot
 * come back after the learner deletes a card. Another waiting copy is never touched.
 */
export const acknowledgeLocalTryDraft = (expectedUserId: string, sessionId: string) =>
  call('acknowledge_local_try_draft', {
    p_expected_user_id: expectedUserId,
    p_session_id: sessionId,
  });
